/**
 * Builds TanStack column definitions from ListViewColumnConfig schema.
 * Extracted from AdvancedTableView to keep file sizes manageable.
 */

import type { ReactNode } from 'react';
import type { ColumnDef, Row } from '@tanstack/react-table';
import type { ListViewColumnConfig } from '@/lib/views/listViewSchemas/types';
import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import { Checkbox } from '@/components/ui/checkbox';

export function buildAdvancedColumns<T>(
  schemaColumns: ListViewColumnConfig[],
  columnOverrides?: Record<string, (props: { row: Row<T> }) => ReactNode>,
): ColumnDef<T>[] {
  const selectColumn: ColumnDef<T> = {
    id: '__select',
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && 'indeterminate')
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
        className="translate-y-0.5"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
        className="translate-y-0.5"
        onClick={(e) => e.stopPropagation()}
      />
    ),
    enableSorting: false,
    enableHiding: false,
    enablePinning: false,
    enableResizing: false,
    size: 40,
  };

  const dataColumns: ColumnDef<T>[] = schemaColumns.map((col) => ({
    id: col.fieldname,
    accessorKey: col.fieldname,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        label={col.label ?? col.fieldname}
      />
    ),
    cell: columnOverrides?.[col.fieldname]
      ? ({ row }) => columnOverrides[col.fieldname]!({ row })
      : ({ row }) => {
          const value = row.getValue(col.fieldname);
          if (value == null || value === '') return <span className="text-muted-foreground">—</span>;
          if (col.type === 'Check') return value === 1 || value === true ? 'Yes' : 'No';
          if (col.type === 'Currency' || col.type === 'Float') {
            return typeof value === 'number' ? value.toLocaleString() : String(value);
          }
          return String(value);
        },
    enableSorting: col.sortable !== false,
    enableHiding: true,
    enableResizing: true,
    size: 150,
    minSize: 60,
    maxSize: 600,
    meta: { label: col.label ?? col.fieldname },
  }));

  return [selectColumn, ...dataColumns];
}
